import React, { useCallback, useEffect, useState } from "react";
import { BarChart3, Lock, Check, Database, AlertTriangle, ShieldCheck } from "lucide-react";
import api from "../../lib/api";

const posCls = (p) => p === "above" ? "text-emerald-700 bg-emerald-50"
  : p === "below" ? "text-red-700 bg-red-50"
  : p === "within" ? "text-amber-700 bg-amber-50" : "text-zinc-500 bg-zinc-50";
const posLabel = (p) => p === "above" ? "Ahead of cohort" : p === "below" ? "Behind cohort"
  : p === "within" ? "In the pack" : "—";

function Band({ title, stats, value, pos }) {
  if (!stats) return null;
  const span = Math.max(stats.max - stats.min, 1);
  const pct = (v) => `${Math.min(100, Math.max(0, ((v - stats.min) / span) * 100))}%`;
  return (
    <div data-testid={`bm-band-${title}`} className="mb-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[12px] font-semibold text-zinc-700">{title}</span>
        {pos && <span className={`text-[10px] px-2 py-0.5 rounded ${posCls(pos)}`}>{posLabel(pos)}</span>}
      </div>
      <div className="relative h-6 bg-zinc-100 rounded">
        <div className="absolute top-0 bottom-0 bg-[var(--e360-primary)]/15 rounded" style={{ left: pct(stats.q1), right: `calc(100% - ${pct(stats.q3)})` }} />
        <div className="absolute top-0 bottom-0 w-0.5 bg-zinc-500" style={{ left: pct(stats.median) }} title={`Cohort median ${stats.median}`} />
        {value != null && <div className="absolute top-0 bottom-0 w-1 bg-[var(--e360-primary)]" style={{ left: pct(value) }} title={`You: ${value}`} />}
      </div>
      <div className="flex justify-between text-[10px] text-zinc-400 mt-0.5">
        <span>{stats.min}</span><span>median {stats.median}</span><span>{stats.max}</span>
      </div>
    </div>
  );
}

export default function BenchmarkPanel({ clientId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [contribs, setContribs] = useState(null);
  const [showContribs, setShowContribs] = useState(false);
  const [realOnly, setRealOnly] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/enterprise360/clients/${clientId}/benchmark?real_only=${realOnly}`)
      .then(({ data }) => setData(data)).catch(() => {});
  }, [clientId, realOnly]);
  useEffect(() => { load(); }, [load]);

  const toggleContribs = useCallback(() => {
    setShowContribs((s) => !s);
    if (!contribs) api.get(`/admin/enterprise360/benchmark/contributions`).then(({ data }) => setContribs(data)).catch(() => {});
  }, [contribs]);

  const toggle = useCallback(async () => {
    setBusy(true);
    try {
      await api.put(`/admin/enterprise360/clients/${clientId}/benchmark-opt-in`, { opt_in: !data?.opt_in });
      load();
    } catch { /* ignore */ }
    setBusy(false);
  }, [clientId, data, load]);

  if (!data) return <div className="text-sm text-zinc-400 py-4">Loading benchmark…</div>;
  const cm = data.client_metrics || {};

  const Cohort = ({ bm, pos }) => (
    <div data-testid={`bm-cohort-${bm.cohort_value || "all"}`} className="border border-zinc-200 rounded-lg p-3 mb-3">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[13px] font-semibold text-zinc-800">Cohort — {bm.cohort_label}</span>
        <span className="text-[11px] text-zinc-400">{bm.n ?? 0} contributor(s)</span>
      </div>
      {bm.sufficient ? (
        <>
          <Band title="Maturity index" stats={bm.maturity} value={cm.avg_current_index} pos={(pos || {}).maturity} />
          <Band title="Confidence score" stats={bm.confidence} value={cm.confidence_score} pos={(pos || {}).confidence} />
          <div className="text-[11px] text-amber-600 mt-2">{bm.provenance}</div>
        </>
      ) : (
        <div data-testid="bm-insufficient" className="text-[12px] text-zinc-500 flex items-center gap-1.5 py-2">
          <Lock size={13} /> {bm.message}
        </div>
      )}
    </div>
  );

  return (
    <div data-testid="benchmark-panel">
      <p className="text-[12px] text-zinc-500 max-w-2xl mb-3">
        <BarChart3 size={12} className="inline mr-1" /> Anonymised cross-engagement benchmarking. Contributions carry no client name or £ figures; a cohort is only shown once it clears the <b>k-anonymity threshold</b> so no single engagement is identifiable.
      </p>

      {data.illustrative && !realOnly && (
        <div data-testid="bm-illustrative-banner" className="flex items-start gap-2 text-[12px] text-orange-700 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 mb-3">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>
            <b>Illustrative benchmark.</b> This cohort relies on seed data pending real opt-in contributions — not for authoritative client comparison.
            {data.governance?.badge && <> Content status: <b>{data.governance.badge.label}</b>.</>}
          </span>
        </div>
      )}
      {!data.illustrative && data.governance?.badge?.verified && (
        <div data-testid="bm-verified-banner" className="flex items-center gap-2 text-[12px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-3">
          <ShieldCheck size={14} /> <span>{data.governance.provenance}</span>
        </div>
      )}

      <div className="flex items-center justify-end mb-3">
        <label className="inline-flex items-center gap-1.5 text-[11px] text-zinc-600 cursor-pointer" data-testid="bm-real-only-toggle">
          <input type="checkbox" checked={realOnly} onChange={(e) => setRealOnly(e.target.checked)} />
          Real contributions only (exclude illustrative seeds)
        </label>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap border border-zinc-200 rounded-lg p-3 mb-4">
        <div className="text-[12px] text-zinc-600">
          Opt-in contributes this engagement's anonymised metrics to the shared cohort.
          <span className="text-zinc-400"> Currently {data.opt_in ? "contributing" : "not contributing"}.</span>
        </div>
        <button data-testid="bm-opt-in-toggle" onClick={toggle} disabled={busy}
          className={`inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded border ${data.opt_in ? "bg-[var(--e360-primary)] text-white border-[var(--e360-primary)]" : "border-zinc-300 text-zinc-600 hover:bg-zinc-50"}`}>
          {data.opt_in ? <><Check size={13} /> Contributing</> : "Opt in to benchmark"}
        </button>
      </div>

      <Cohort bm={data.sector_benchmark} pos={data.sector_position} />
      <Cohort bm={data.all_benchmark} pos={data.all_position} />

      <button data-testid="bm-contribs-toggle" onClick={toggleContribs}
        className="mt-2 inline-flex items-center gap-1.5 text-[12px] text-[var(--e360-primary)] hover:underline">
        <Database size={13} /> {showContribs ? "Hide" : "View"} cohort contributions (anonymised)
      </button>
      {showContribs && contribs && (
        <div data-testid="bm-contributions" className="mt-2 border border-zinc-200 rounded-lg p-3">
          <div className="text-[11px] text-zinc-500 mb-2">{contribs.total} contribution(s) · {contribs.real} live · {contribs.seed} illustrative seed. No client identity is stored — only a non-reversible hash.</div>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead><tr className="text-zinc-400 text-left"><th className="py-1 pr-3">Hash</th><th className="pr-3">Sector</th><th className="pr-3">Maturity</th><th className="pr-3">Confidence</th><th>Origin</th></tr></thead>
              <tbody>
                {contribs.contributions.map((c) => (
                  <tr key={c.client_hash} className="border-t border-zinc-100">
                    <td className="py-1 pr-3 font-mono text-zinc-500">{c.client_hash}</td>
                    <td className="pr-3">{c.sector}</td>
                    <td className="pr-3">{c.metrics?.avg_current_index ?? "—"}</td>
                    <td className="pr-3">{c.metrics?.confidence_score ?? "—"}</td>
                    <td className="text-zinc-400">{c.seed ? "seed" : "live"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
